import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Mail, Phone, ArrowRight, Sparkles, BarChart3, Zap, CheckCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { SiteDataProvider, useSiteData } from '../context/SiteDataContext';

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NYZA_LOGO_URL = "/images/nyza-logo.webp";

// Navigation Component - Same as main site
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6 bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="relative z-10">
            <img 
              src={NYZA_LOGO_URL} 
              alt="NYZA Studio" 
              className="h-8 md:h-10"
              style={{ filter: 'brightness(0)' }}
            />
          </Link>
          
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <Link to="/" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">
              HOME
            </Link>
            <Link to="/references" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">
              REFERENCES
            </Link>
            <Link to="/products" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">
              PRODUCTS
            </Link>
            <Link to="/marketing" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-900 bg-white shadow-sm">
              MARKETING
            </Link>
          </div>
          
          <Link to="/#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">
            CONTACT
          </Link>
          
          <button className="md:hidden p-2 text-gray-900" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4"
          >
            <Link to="/" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>HOME</Link>
            <Link to="/references" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>REFERENCES</Link>
            <Link to="/products" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>PRODUCTS</Link>
            <Link to="/marketing" className="block py-2 text-sm text-purple-600 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>MARKETING</Link>
            <Link to="/#contact" className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase tracking-wide px-4 py-2 rounded-full" onClick={() => setIsOpen(false)}>CONTACT</Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Contact Section Component
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', surname: '', email: '', phone: '',
    company: '', budget: '', message: '', privacy_accepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy_accepted) {
      toast.error('Please accept the privacy policy');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        company: formData.company,
        service: formData.budget || 'Marketing Inquiry',
        message: formData.message,
        privacy_accepted: formData.privacy_accepted
      });
      toast.success("Message sent successfully!");
      setFormData({ name: '', surname: '', email: '', phone: '', company: '', budget: '', message: '', privacy_accepted: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending message.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F0FF 30%, #E8D4FF 70%, #D4B8FF 100%)' }}>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-purple-300/30 blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-purple-400/20 blur-[120px]" />
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="hidden lg:block">
            <div className="relative">
              <img src="/images/contact.jpg" alt="Contact" className="rounded-2xl shadow-2xl relative z-10 w-full object-cover" loading="lazy" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              SHOOT US<br/><span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">A MESSAGE</span>
            </h2>
            <p className="text-gray-500 text-sm mb-4">Ready to take your brand to the next level? Get in touch and let's build something that truly performs.</p>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <a href="mailto:workwith@nyza-studio.at" className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 transition-colors">
                <Mail className="w-4 h-4" />workwith@nyza-studio.at
              </a>
              <a href="https://wa.me/message/QRDZ3NTL345SA1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 transition-colors">
                <Phone className="w-4 h-4" />+43 660 5502578
              </a>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="First Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all bg-white/80" />
                <input type="text" placeholder="Last Name *" value={formData.surname} onChange={(e) => setFormData({...formData, surname: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all bg-white/80" />
              </div>
              <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all bg-white/80" />
              <input type="text" placeholder="Company (optional)" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all bg-white/80" />
              <textarea placeholder="Your Message *" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none bg-white/80" />
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.privacy_accepted} onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})} className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                <span className="text-xs text-gray-500">I accept the <Link to="/datenschutz" className="text-purple-600 hover:underline">Privacy Policy</Link> *</span>
              </label>
              
              <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50">
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { content } = useSiteData();
  
  return (
    <footer className="py-16 px-4" style={{ background: 'linear-gradient(180deg, #D4B8FF 0%, #9A45FF 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-10 mb-4" style={{ filter: 'brightness(0) invert(1)' }} />
            <p className="text-white/80 text-sm max-w-md">Elevating brands through high-voltage creativity and precision digital engineering.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Services</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li><Link to="/marketing" className="hover:text-white transition-colors">Marketing</Link></li>
              <li><Link to="/products" className="hover:text-white transition-colors">Products</Link></li>
              <li><Link to="/schedule-demo" className="hover:text-white transition-colors">Book a Demo</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Legal</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li><Link to="/impressum" className="hover:text-white transition-colors">Imprint</Link></li>
              <li><Link to="/datenschutz" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/agb" className="hover:text-white transition-colors">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/20 pt-8 text-center">
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} NYZA Studio. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main Marketing Page Content
const MarketingContent = () => {
  return (
    <div className="min-h-screen bg-[#f9f9f9]">
      <Navigation />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative px-6 pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="z-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 font-medium text-xs uppercase tracking-widest mb-6">
              High-Voltage Growth
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-[0.95] tracking-tight text-gray-900 mb-8">
              The New <span className="text-purple-600 italic">Standard</span> for Digital Presence.
            </h1>
            <p className="text-xl text-gray-600 max-w-lg mb-10 leading-relaxed">
              We don't just run ads; we curate high-end digital experiences that convert attention into brand loyalty through precision marketing and creative strategy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="#services" className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-700 hover:to-purple-800 transition-all flex items-center gap-2">
                Explore Services <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-700 bg-white p-4">
              <img 
                alt="Marketing Dashboard" 
                className="rounded-xl w-full"
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop&q=80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 space-y-32">
        {/* Service 01 */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <h2 className="text-purple-600 font-bold text-xl mb-4">01 / Full Social Ecosystem</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">Mastering the Art of Narrative.</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our social strategy goes beyond posting schedules. We build living ecosystems where your brand voice dominates the conversation through cinematic content and strategic engagement.
              </p>
              <ul className="space-y-4 mb-10">
                <li className="flex items-center gap-3 text-gray-900">
                  <CheckCircle className="w-5 h-5 text-purple-600" /> Viral Content Production
                </li>
                <li className="flex items-center gap-3 text-gray-900">
                  <CheckCircle className="w-5 h-5 text-purple-600" /> Influence Architecture
                </li>
                <li className="flex items-center gap-3 text-gray-900">
                  <CheckCircle className="w-5 h-5 text-purple-600" /> 24/7 Community Intelligence
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-gray-100 p-2 transform lg:translate-x-12">
                <img 
                  alt="Social Media" 
                  className="rounded-xl w-full object-cover aspect-[4/3]"
                  src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=600&fit=crop&q=80"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Service 02 */}
        <div className="bg-gray-50 py-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="rounded-2xl overflow-hidden shadow-2xl bg-white p-2 transform lg:-translate-x-12">
                  <img 
                    alt="Data Analytics" 
                    className="rounded-xl w-full object-cover aspect-[4/3]"
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=80"
                  />
                </div>
              </div>
              <div className="lg:col-span-5">
                <h2 className="text-green-600 font-bold text-xl mb-4">02 / Algorithmic Precision</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">Audience Profiling Reimagined.</h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  We utilize proprietary AI to identify micro-segments of your market that others overlook. Our tailoring is surgical, ensuring every dollar spent resonates with a high-intent buyer.
                </p>
                <div className="p-6 bg-white rounded-xl border-l-4 border-green-500 shadow-sm">
                  <p className="italic text-gray-600">"The level of detail in Nyza's audience mapping increased our conversion rate by 42% in three months."</p>
                  <p className="font-bold text-gray-900 mt-4">— Director of Growth, Aura Luxe</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service 03 */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <h2 className="text-purple-600 font-bold text-xl mb-4">03 / Identity Expansion</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">Brand Systems That Scale.</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Growth branding is about future-proofing. We create visual and linguistic systems that allow your brand to evolve across new platforms without losing its core creative soul.
              </p>
              <Link to="/references" className="group flex items-center gap-4 font-bold text-purple-600 uppercase text-sm tracking-wider">
                View Case Studies
                <span className="w-12 h-[2px] bg-purple-600 group-hover:w-20 transition-all duration-300"></span>
              </Link>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-gray-100 p-2 transform lg:translate-x-12 lg:-rotate-2">
                <img 
                  alt="Brand Identity" 
                  className="rounded-xl w-full object-cover aspect-[4/3]"
                  src="https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=600&fit=crop&q=80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#9A45FF] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="mb-16">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">The Nyza Advantage</h2>
            <p className="text-xl max-w-2xl text-white/80">We operate at the intersection of high-end aesthetics and hard-data performance.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-md p-10 rounded-2xl border border-white/10 hover:bg-white/20 transition-colors duration-300">
              <Sparkles className="w-10 h-10 mb-6" />
              <h4 className="text-2xl font-bold mb-4">Creative First</h4>
              <p className="text-white/70 leading-relaxed">We believe beauty is a utility. Our designs don't just look good—they're engineered to hold attention in a split-second world.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-10 rounded-2xl border border-white/10 hover:bg-white/20 transition-colors duration-300">
              <BarChart3 className="w-10 h-10 mb-6" />
              <h4 className="text-2xl font-bold mb-4">Data Obsessed</h4>
              <p className="text-white/70 leading-relaxed">Every creative choice is backed by performance metrics. We iterate rapidly based on real-time user behavior patterns.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-10 rounded-2xl border border-white/10 hover:bg-white/20 transition-colors duration-300">
              <Zap className="w-10 h-10 mb-6" />
              <h4 className="text-2xl font-bold mb-4">Speed to Market</h4>
              <p className="text-white/70 leading-relaxed">Our sprint-based model means your campaigns go from concept to live in days, not months. We move at the speed of the internet.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Export with Provider
export default function Marketing() {
  return (
    <SiteDataProvider>
      <MarketingContent />
    </SiteDataProvider>
  );
}
